import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Popconfirm,
  message,
  Alert,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import productService from '../../services/productService';
import pricingGroupService from '../../services/pricingGroupService';
import productPricingSlabMatrixService from '../../services/productPricingSlabMatrixService';
import useRemoteSelectPage from '../../hooks/useRemoteSelectPage';

const SELECT_PAGE_SIZE = 50;

const getProductId = (product) => product?.id;

export default function ProductSlabMatrixPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_SLAB_MATRIX_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_SLAB_MATRIX_EDIT);

  const [groups, setGroups] = useState([]);
  const [productId, setProductId] = useState(undefined);
  const [pricingGroupId, setPricingGroupId] = useState(undefined);
  const [slabs, setSlabs] = useState([]);
  const [slabMatrixError, setSlabMatrixError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const isEdit = Boolean(editing);

  const maxOrder = slabs.length ? Math.max(...slabs.map((s) => Number(s.slab_order) || 0)) : 0;

  const lastSlab = slabs.length
    ? [...slabs].sort((a, b) => (Number(a.slab_order) || 0) - (Number(b.slab_order) || 0)).at(-1)
    : null;

  const nextMinQty = !lastSlab
    ? 1
    : lastSlab.max_qty == null || lastSlab.max_qty === ''
      ? null
      : Number(lastSlab.max_qty) + 1;

  const minQtyLocked = Boolean(!editing && lastSlab && nextMinQty != null);

  const fetchProductsPage = useCallback(async ({ page, limit, search, signal }) => {
    const response = await productService.getProducts({
      page,
      limit,
      search,
      sort_by: 'preference_index',
      sort_order: 'asc',
      signal,
    });
    const data = response?.data ?? response;
    const list = data?.products ?? data?.data ?? data?.items ?? [];
    return {
      items: Array.isArray(list) ? list : [],
      pagination: data?.pagination,
    };
  }, []);

  const {
    items: products,
    loading: productsLoading,
    handleSearch: handleProductSearch,
    handlePopupScroll: handleProductPopupScroll,
    handleOpenChange: handleProductOpenChange,
    dropdownRender: productDropdownRender,
    searchValue: productSearchValue,
    clearSearch: clearProductSearch,
  } = useRemoteSelectPage({
    fetchPage: fetchProductsPage,
    getKey: getProductId,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: productId,
    onError: (e) => message.error(e?.response?.data?.message || e?.message || 'Failed to load products'),
  });

  const handleProductChange = useCallback((value) => {
    setProductId(value);
  }, []);

  const handleProductClear = useCallback(() => {
    setProductId(undefined);
    clearProductSearch();
  }, [clearProductSearch]);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const gList = await pricingGroupService.list();
      setGroups(Array.isArray(gList) ? gList : []);
    } catch (e) {
      message.error(e?.message || 'Failed to load pricing groups');
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const loadSlabs = useCallback(async () => {
    if (!productId || !pricingGroupId) {
      setSlabs([]);
      setSlabMatrixError(null);
      return;
    }
    setLoading(true);
    setSlabMatrixError(null);
    try {
      const { slabs: list, slabMatrixValid, slabMatrixError: err } =
        await productPricingSlabMatrixService.list(productId, pricingGroupId);
      setSlabs(Array.isArray(list) ? list : []);
      if (slabMatrixValid === false && err) {
        setSlabMatrixError(err);
      } else {
        setSlabMatrixError(null);
      }
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load slabs');
      setSlabs([]);
      setSlabMatrixError(null);
    } finally {
      setLoading(false);
    }
  }, [productId, pricingGroupId]);

  useEffect(() => {
    loadSlabs();
  }, [loadSlabs]);

  const openCreate = () => {
    if (lastSlab && (lastSlab.max_qty == null || lastSlab.max_qty === '')) {
      message.warning('The last slab is open-ended. Edit it to set a max qty before adding a new slab.');
      return;
    }
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      slab_order: maxOrder + 1 || 1,
      product_id: productId,
      pricing_group_id: pricingGroupId,
      min_qty: nextMinQty ?? 1,
    });
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      slab_order: record.slab_order,
      min_qty: record.min_qty,
      max_qty: record.max_qty,
      mark_up_percent: record.mark_up_percent,
    });
    setDrawerOpen(true);
  };

  const onClose = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (isEdit) {
        const res = await productPricingSlabMatrixService.update(editing.id, {
          slab_order: values.slab_order,
          min_qty: values.min_qty,
          max_qty: values.max_qty === undefined || values.max_qty === '' ? null : values.max_qty,
          mark_up_percent: values.mark_up_percent,
        });
        if (res?.success === false) {
          message.error(res?.message || 'Update failed');
          return;
        }
        message.success('Slab updated');
      } else {
        const res = await productPricingSlabMatrixService.create({
          product_id: values.product_id,
          pricing_group_id: values.pricing_group_id,
          slab_order: values.slab_order,
          min_qty: values.min_qty,
          max_qty: values.max_qty === undefined || values.max_qty === '' ? null : values.max_qty,
          mark_up_percent: values.mark_up_percent,
        });
        if (res?.success === false) {
          message.error(res?.message || 'Create failed');
          return;
        }
        message.success('Slab created');
        setProductId(values.product_id);
        setPricingGroupId(values.pricing_group_id);
      }
      onClose();
      loadSlabs();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (record) => {
    try {
      const res = await productPricingSlabMatrixService.remove(record.id);
      if (res?.success === false) {
        message.error(res?.message || 'Delete failed');
        return;
      }
      message.success('Slab deleted');
      loadSlabs();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        label: p.name || p.slug || p.id,
      })),
    [products]
  );

  const groupOptions = useMemo(
    () =>
      groups.map((g) => ({
        value: g.id,
        label: g.group_name || g.id,
      })),
    [groups]
  );

  const formatQty = (v) => String(Math.round(Number(v)));

  const columns = [
    { title: 'Order', dataIndex: 'slab_order', key: 'slab_order', width: 80 },
    {
      title: 'Min qty',
      dataIndex: 'min_qty',
      key: 'min_qty',
      render: (v) => formatQty(v),
    },
    {
      title: 'Max qty',
      dataIndex: 'max_qty',
      key: 'max_qty',
      render: (v) => (v == null || v === '' ? '— (open)' : formatQty(v)),
    },
    {
      title: 'Mark-up %',
      dataIndex: 'mark_up_percent',
      key: 'mark_up_percent',
      render: (v) => (v == null || v === '' ? '—' : Number(v).toFixed(2)),
    },
    {
      title: 'Actions',
      key: 'act',
      width: 200,
      render: (_, r) => (
        <Space>
          {canEdit && (
            <Button type="default" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
              Edit
            </Button>
          )}
          {canEdit && (
            <Popconfirm
              title="Delete this slab?"
              onConfirm={() => onDelete(r)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Product Slab Matrix</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Each row is a <strong>quantity</strong> band: min / max units. New slabs auto-start at the previous slab&apos;s{' '}
          <strong>max_qty + 1</strong> so slabs stay contiguous (no gap, no overlap). Only the last slab may omit{' '}
          <strong>max_qty</strong> (open-ended). Slab order is 1, 2, 3…
        </p>
      </div>

      {slabMatrixError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Slab range validation failed"
          description={slabMatrixError}
        />
      )}

      <div className="flex flex-wrap gap-4 items-end mb-4 bg-white p-4 rounded-2xl border border-gray-100">
        <div className="min-w-[200px] flex-1">
          <div className="text-xs text-gray-500 mb-1">Product</div>
          <Select
            showSearch
            autoClearSearchValue={false}
            placeholder="Select product"
            optionFilterProp="label"
            filterOption={false}
            className="w-full"
            loading={productsLoading}
            value={productId}
            onChange={handleProductChange}
            onClear={handleProductClear}
            searchValue={productSearchValue}
            onSearch={handleProductSearch}
            onPopupScroll={handleProductPopupScroll}
            onOpenChange={handleProductOpenChange}
            dropdownRender={productDropdownRender}
            options={productOptions}
            allowClear
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <div className="text-xs text-gray-500 mb-1">Pricing group</div>
          <Select
            showSearch
            placeholder="Select group"
            optionFilterProp="label"
            className="w-full"
            loading={groupsLoading}
            value={pricingGroupId}
            onChange={setPricingGroupId}
            options={groupOptions}
            allowClear
          />
        </div>
        {canCreate && productId && pricingGroupId && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add slab
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-0 overflow-hidden">
        <Table
          rowKey={(r) => r.id}
          columns={columns}
          dataSource={slabs}
          loading={loading}
          pagination={false}
          locale={{ emptyText: productId && pricingGroupId ? 'No slabs. Add a slab to begin.' : 'Select product and pricing group.' }}
        />
      </div>

      <Drawer
        title={isEdit ? 'Edit slab' : 'New slab'}
        width={400}
        open={drawerOpen}
        onClose={onClose}
        extra={
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={onSubmit}>
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          {!isEdit && (
            <>
              <Form.Item name="product_id" label="Product" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch
                  autoClearSearchValue={false}
                  options={productOptions}
                  optionFilterProp="label"
                  filterOption={false}
                  placeholder="Product"
                  loading={productsLoading}
                  searchValue={productSearchValue}
                  onSearch={handleProductSearch}
                  onClear={clearProductSearch}
                  onPopupScroll={handleProductPopupScroll}
                  onOpenChange={handleProductOpenChange}
                  dropdownRender={productDropdownRender}
                />
              </Form.Item>
              <Form.Item
                name="pricing_group_id"
                label="Pricing group"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select showSearch options={groupOptions} optionFilterProp="label" placeholder="Group" />
              </Form.Item>
            </>
          )}
          <Form.Item name="slab_order" label="Slab order" rules={[{ required: true, message: 'Required' }]}>
            <Input type="number" min={1} step={1} />
          </Form.Item>
          <Form.Item
            name="min_qty"
            label="Min quantity"
            rules={[{ required: true, message: 'Required' }]}
            extra={
              minQtyLocked
                ? `Auto-set to previous slab's max qty (${lastSlab.max_qty}) + 1 to keep slabs contiguous.`
                : undefined
            }
          >
            <InputNumber className="w-full" min={1} precision={0} step={1} disabled={minQtyLocked} />
          </Form.Item>
          <Form.Item name="max_qty" label="Max quantity (empty for last / open-end tier)">
            <InputNumber className="w-full" min={1} precision={0} step={1} allowClear />
          </Form.Item>
          <Form.Item
            name="mark_up_percent"
            label="Mark-up % (2 decimal places)"
            rules={[
              { required: true, message: 'Required' },
              {
                validator: (_, v) => {
                  if (v == null || v === '') return Promise.resolve();
                  const s = String(v).trim();
                  if (s.includes('.')) {
                    const frac = s.split('.')[1] || '';
                    if (frac.length > 2) {
                      return Promise.reject(new Error('At most 2 decimal places'));
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber className="w-full" min={0} precision={2} step={0.01} />
          </Form.Item>
        </Form>
      </Drawer>
    </DashboardPageWrapper>
  );
}
